import React, { useEffect, useState } from 'react';
import { Table, Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Apify } from '../Apify';
import I18N from '../I18N';
import { T } from '../traks';
import XTab from './XTab';
import { R } from '../routes';
import { STATIC_WEEKLY_WINNER_FULL_LIMIT, STATIC_WEEKLY_WINNER_PREVIEW_LIMIT, supportsStaticWeeklyWinners } from '../GameConfig';


const WeeklyWinnersResource = Apify('/z/weekly_winners', {})
  .with_query_from_props(({ groupId, week, year, limit }) => ({
    group_id: groupId,
    week: week || 0,
    year: year || 0,
    limit: limit || STATIC_WEEKLY_WINNER_FULL_LIMIT,
  }))
  .with_response_prop('report')
  .with_4xx_prop('weeklyWinnerError');

function WeeklyWinnersPreviewLoaderBody({ report, weeklyWinnerError, onLoad }) {
  useEffect(() => {
    if (onLoad) {
      onLoad({ report, weeklyWinnerError });
    }
  }, [report, weeklyWinnerError, onLoad]);

  return null;
}

const WeeklyWinnersPreviewLoaderResource = WeeklyWinnersResource.wrap(
  WeeklyWinnersPreviewLoaderBody,
  () => null
);

export function WeeklyWinnersPreviewLoader(props) {
  return <WeeklyWinnersPreviewLoaderResource {...props} />;
}

const WeeklyWinnerTradesResource = Apify('/z/weekly_winner_trades', {})
  .with_query_from_props(({ groupId, week, year, portfolioId }) => ({
    group_id: groupId,
    week: week || 0,
    year: year || 0,
    portfolio_id: portfolioId || 0,
  }))
  .with_predicate(({ isOpen, portfolioId }) => isOpen && !!portfolioId)
  .with_response_prop('tradeReport')
  .with_4xx_prop('tradeError');

export function GroupWeeklyWinnerTabs({ groupId, selected, onSelect }) {
  if (!supportsStaticWeeklyWinners(groupId)) {
    return null;
  }

  const handleSelect = (index) => {
    if (onSelect) {
      onSelect(index);
      return;
    }

    if (index === 0) {
      R.go('/group_leaderboard_full/:group_id', { group_id: groupId });
      return;
    }

    R.go('/group_weekly_winners/:group_id', { group_id: groupId });
  };

  return (
    <div className="ad_group_weekly_tabs">
      <XTab
        row_classes={["pb-2", "no_unselected_border"]}
        add_classes={["white", "ad_group_weekly_tab"]}
        spacing_classes={["px-3"]}
        selected={selected || 0}
        onSelect={handleSelect}
        tabs={[<span>Stilling</span>, <span>Ugevindere</span>]}
      />
    </div>
  );
}

function WeeklyGrowthPct({ value }) {
  const negative = value < 0;
  const className = negative ? 'txt-color-red fw-bold' : 'txt-color-green fw-bold';
  const arrow = negative ? '▼' : '▲';
  return <span className={className}>{arrow} {I18N.floatval(value, 2)}%</span>;
}

export function NoWeeklyWinners({ error }) {
  if (error && error.http_status !== 404) {
    return <div className="text-danger py-3">Kunne ikke hente ugevindere.</div>;
  }
  return <div className="text-muted py-3">Ugevinderne vises, når første uge er afsluttet.</div>;
}

export function WeeklyWinnersLatestWeek({ report, weeklyWinnerError }) {
  if (weeklyWinnerError || !report) {
    return null;
  }

  return (
    <div>
      <span className="pr-2">Seneste uge</span>
      <b>{report.Week}</b> / <b>{report.Year}</b>
    </div>
  );
}

function WeeklyWinnerTradesModal({ groupId, week, year, winner, isOpen, toggle }) {
  const Body = WeeklyWinnerTradesResource.wrap(({ tradeReport, tradeError }) => {
    if (tradeError) {
      return <div className="text-danger">Kunne ikke hente handler.</div>;
    }

    const trades = tradeReport && tradeReport.Trades ? tradeReport.Trades : [];
    if (trades.length === 0) {
      return <div className="text-muted">Der er ikke registreret handler for denne uge.</div>;
    }

    return (
      <Table className="ad_leader_board bg-color-white mb-0">
        <thead>
          <tr>
            <th><span className="ad_game_table_header font-s">Dato</span></th>
            <th><span className="ad_game_table_header font-s">Handling</span></th>
            <th><span className="ad_game_table_header font-s">Aktie</span></th>
            <th className="text-end"><span className="ad_game_table_header font-s">Antal</span></th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade, i) => (
            <tr className="ad_game_table_border" key={`${trade.Timestamp}-${i}`}>
              <td>{trade.Date}</td>
              <td className={trade.TradeType === 'Køb' ? 'txt-color-green fw-bold' : 'txt-color-red fw-bold'}>{trade.TradeType}</td>
              <td>{trade.StockName}</td>
              <td className="text-end">{I18N.floatval(trade.Quantity, 0)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  });

  if (!winner) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Handler i uge {week}</ModalHeader>
      <ModalBody>
        <div className="fw-bold pb-3">{winner.Username}</div>
        <Body groupId={groupId} week={week} year={year} portfolioId={winner.PortfolioID} isOpen={isOpen} />
      </ModalBody>
    </Modal>
  );
}

function WeeklyWinnerRows({ report, limit }) {
  const [selectedWinner, setSelectedWinner] = useState(null);
  const winners = report && report.Winners ? report.Winners.slice(0, limit || report.Winners.length) : [];
  const currency = report && report.Currency ? report.Currency : 'kr.';

  return (
    <React.Fragment>
      <Table className="ad_leader_board bg-color-white">
        <thead>
          <tr>
            <th className="ad_th_1px"><span className="ad_game_table_header font-s">Rank</span></th>
            <th><span className="ad_game_table_header font-s pl-3">Navn</span></th>
            <th className="d-none d-md-table-cell"><span className="ad_game_table_header font-s">Sidste uges værdi</span></th>
            <th><span className="ad_game_table_header font-s">Denne uges værdi</span></th>
            <th><span className="ad_game_table_header font-s">Vækst %</span></th>
            <th className="text-end"><span className="ad_game_table_header font-s">Handlinger</span></th>
          </tr>
        </thead>
        <tbody>
          {winners.map((winner) => (
            <tr className="ad_game_table_border ad_grey_hover" key={winner.PortfolioID}>
              <td className="text-right fw-bold">{winner.Rank}</td>
              <td className="pl-4 mr-0 align-middle table-cell-min py-md-2 fw-bold">
                <span className="text-overflow-dynamic-container">
                  <span className="text-overflow-dynamic-ellipsis">{winner.Username}</span>
                </span>
              </td>
              <td className="d-none d-md-table-cell fw-bold">{I18N.floatval_unit(winner.OldValue, 2, currency)}</td>
              <td className="fw-bold">{I18N.floatval_unit(winner.NewValue, 2, currency)}</td>
              <td><WeeklyGrowthPct value={winner.GrowthPct} /></td>
              <td className="text-end">
                <a className="txt-color-green" style={{ cursor: 'pointer' }} onClick={() => setSelectedWinner(winner)}>Se handler</a>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <WeeklyWinnerTradesModal
        groupId={report.GroupGameID}
        week={report.Week}
        year={report.Year}
        winner={selectedWinner}
        isOpen={!!selectedWinner}
        toggle={() => setSelectedWinner(null)}
      />
    </React.Fragment>
  );
}

export function WeeklyWinnersPreview({ report, weeklyWinnerError, isLoaded, limit }) {
  if (!isLoaded) {
    return <div><T>Loading...</T></div>;
  }

  if (weeklyWinnerError || !report) {
    return <NoWeeklyWinners error={weeklyWinnerError} />;
  }

  return <WeeklyWinnerRows report={report} limit={limit || STATIC_WEEKLY_WINNER_PREVIEW_LIMIT} />;
}

export function WeeklyWinnersFull({ groupId, showHeader }) {
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const renderHeader = showHeader !== false;

  const Body = WeeklyWinnersResource.wrap(({ report, weeklyWinnerError }) => {
    if (weeklyWinnerError || !report) {
      return <NoWeeklyWinners error={weeklyWinnerError} />;
    }

    const weeks = report.Weeks || [];
    const currentIndex = weeks.findIndex(w => w.Week === report.Week && w.Year === report.Year);
    const olderWeek = currentIndex >= 0 && currentIndex + 1 < weeks.length ? weeks[currentIndex + 1] : null;
    const newerWeek = currentIndex > 0 ? weeks[currentIndex - 1] : null;

    const selectWeek = (week) => {
      if (!week) return;
      setSelectedWeek(week.Week);
      setSelectedYear(week.Year);
    };

    return (
      <React.Fragment>
        {renderHeader && (
          <div className="d-flex flex-wrap align-items-center justify-content-between pb-3">
            <div>
              <div className="pb-0 ad_150pct mr-auto">
                <span className="fw-bold"><T>Pool</T></span> — {report.Name}
              </div>
              <GroupWeeklyWinnerTabs groupId={groupId} selected={1} />
              <div>Historiske ugevindere · Uge {report.Week} / {report.Year} · Top {report.Limit}</div>
            </div>
          </div>
        )}

        {!renderHeader && <div className="pb-3">Historiske ugevindere · Uge {report.Week} / {report.Year} · Top {report.Limit}</div>}

        <WeeklyWinnerRows report={report} limit={report.Limit} />

        <div className="d-flex justify-content-between align-items-center my-3">
          <button
            className="xyzzy_button btn btn-outline-light"
            disabled={!olderWeek}
            onClick={() => selectWeek(olderWeek)}
          >
            Forrige
          </button>
          <div>Uge {report.Week} / {report.Year}</div>
          <button
            className="xyzzy_button btn btn-outline-light"
            disabled={!newerWeek}
            onClick={() => selectWeek(newerWeek)}
          >
            Næste
          </button>
        </div>
      </React.Fragment>
    );
  });

  return <Body groupId={groupId} week={selectedWeek || 0} year={selectedYear || 0} limit={STATIC_WEEKLY_WINNER_FULL_LIMIT} />;
}



// WEBPACK FOOTER //
// ./src/c/WeeklyWinners.js